import React, { useState, useRef } from 'react';
import './UploadItemsModal.css';
import {
  FaUpload,
  FaDownload,
  FaTimes,
  FaFileAlt,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTrash
} from 'react-icons/fa';

/**
 * Sample CSV Template Content
 */
const SAMPLE_CSV_CONTENT = `Item Name,Batch No,Expiry Date,Pack Quantity,Quantity,Free,MRP,Rate,Discount %,GST %,HSN Code
Dolo 650 Tablet,DL-901,2027-08-31,15,10,1,30.50,24.15,0,12,300490
Augmentin 625 Duo,AUG-442,2026-12-31,10,5,0,201.71,161.40,5,12,300410
Pan 40 Tablet,PAN-102,2027-05-31,15,8,0,155.00,120.00,2,12,300490
Azithral 500 Tablet,AZI-781,2027-02-28,5,4,0,119.50,95.00,0,12,300420
Telma 40 Tablet,TEL-650,2027-09-30,30,6,0,220.00,176.10,0,12,300490
Monocef 1g Injection,MON-321,2026-11-30,1,20,2,65.00,48.00,0,12,300420`;

/**
 * Robust CSV Line Parser that handles quoted values with commas
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Header normalizer to match various column name spellings
 */
function normalizeHeaderName(h) {
  return String(h || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Normalizes user expiry input to YYYY-MM-DD for database compatibility
 */
function normalizeExpiryDate(raw) {
  if (!raw || !raw.trim()) return '';
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}$/.test(s)) {
    const parts = s.split('-').map(Number);
    const lastDay = new Date(parts[0], parts[1], 0).getDate();
    return `${s}-${String(lastDay).padStart(2, '0')}`;
  }
  if (/^\d{1,2}[/-]\d{4}$/.test(s)) {
    const parts = s.split(/[/-]/).map(Number);
    const m = parts[0];
    const y = parts[1];
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  }
  if (/^\d{1,2}[/-]\d{2}$/.test(s)) {
    const parts = s.split(/[/-]/).map(Number);
    const m = parts[0];
    const y = 2000 + parts[1];
    const lastDay = new Date(y, m, 0).getDate();
    return `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  }
  return s;
}

const UploadItemsModal = ({
  isOpen,
  onClose,
  onImportItems,
  existingItemsCount = 0
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [parseError, setParseError] = useState('');
  const [importMode, setImportMode] = useState('append'); // 'append' | 'replace'
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  // ─── 1. Download Sample Reference Template ─────────────────────────────────
  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV_CONTENT], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Purchase_Order_Items_Sample_Template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ─── 2. Process and Parse CSV File ─────────────────────────────────────────
  const processCSVFile = (file) => {
    setParseError('');
    setParsedItems([]);

    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv') && file.type && !file.type.includes('csv') && !file.type.includes('text')) {
      setParseError('Please upload a valid .CSV format file.');
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        if (!text || !text.trim()) {
          setParseError('The selected file is empty.');
          return;
        }

        const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim().length > 0);
        if (lines.length < 2) {
          setParseError('The CSV file must contain a header row and at least one item data row.');
          return;
        }

        // Parse Header
        const rawHeaders = parseCSVLine(lines[0]);
        const headerMap = {};
        rawHeaders.forEach((h, idx) => {
          const norm = normalizeHeaderName(h);
          if (norm.includes('item') || norm.includes('medicine') || norm === 'name' || norm === 'particulars') {
            headerMap.itemName = idx;
          } else if (norm.includes('batch')) {
            headerMap.batchNo = idx;
          } else if (norm.includes('exp')) {
            headerMap.expiryDate = idx;
          } else if (norm.includes('packqty') || norm.includes('packquantity') || norm === 'pack') {
            headerMap.packQuantity = idx;
          } else if (norm === 'qty' || norm === 'quantity' || norm === 'packs' || norm === 'orderedqty') {
            headerMap.quantity = idx;
          } else if (norm.includes('free')) {
            headerMap.freeQuantity = idx;
          } else if (norm === 'mrp' || norm.includes('mrppack') || norm.includes('mrpunit')) {
            headerMap.mrp = idx;
          } else if (norm === 'rate' || norm.includes('packrate') || norm.includes('price') || norm.includes('cost')) {
            headerMap.rate = idx;
          } else if (norm.includes('disc')) {
            headerMap.discountPercent = idx;
          } else if (norm.includes('gst') || norm.includes('tax')) {
            headerMap.gstPercent = idx;
          } else if (norm.includes('hsn')) {
            headerMap.hsnCode = idx;
          }
        });

        if (headerMap.itemName === undefined) {
          setParseError('Could not find an "Item Name" column. Please check column headers or use our sample template.');
          return;
        }

        const items = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = parseCSVLine(lines[i]);
          if (cols.length === 0 || cols.every((c) => !c)) continue;

          const itemName = cols[headerMap.itemName] || '';
          if (!itemName.trim()) continue;

          const packQty = headerMap.packQuantity !== undefined ? Math.max(1, Number(cols[headerMap.packQuantity]) || 10) : 10;
          const qty = headerMap.quantity !== undefined ? Math.max(1, Number(cols[headerMap.quantity]) || 1) : 1;
          const freeQty = headerMap.freeQuantity !== undefined ? Number(cols[headerMap.freeQuantity]) || 0 : 0;
          const mrp = headerMap.mrp !== undefined ? Number(cols[headerMap.mrp]) || 0 : 0;
          const rate = headerMap.rate !== undefined ? Number(cols[headerMap.rate]) || 0 : 0;
          const disc = headerMap.discountPercent !== undefined ? Number(cols[headerMap.discountPercent]) || 0 : 0;
          const gst = headerMap.gstPercent !== undefined ? Number(cols[headerMap.gstPercent]) || 12 : 12;
          const batchNo = headerMap.batchNo !== undefined ? cols[headerMap.batchNo] || '' : '';
          const expiryDate = headerMap.expiryDate !== undefined ? normalizeExpiryDate(cols[headerMap.expiryDate]) : '';
          const hsnCode = headerMap.hsnCode !== undefined ? cols[headerMap.hsnCode] || '300490' : '300490';

          const packRate = rate;
          const unitRate = packQty > 0 ? (packRate / packQty) : packRate;

          items.push({
            id: Date.now() + Math.random(),
            itemName: itemName.trim(),
            rawName: itemName.trim(),
            brand: '',
            composition: '',
            hsn: hsnCode,
            hsnCode: hsnCode,
            packType: packQty > 1 ? 'STRIP' : 'UNIT',
            packQuantity: packQty,
            quantity: qty,
            freeQuantity: freeQty,
            mrp: mrp,
            rate: rate,
            packRate: packRate,
            ratePerUnit: unitRate,
            discountPercent: disc,
            discountType: '%',
            gstPercent: gst,
            batchNo: batchNo.trim(),
            expiryDate: expiryDate.trim(),
            stockQuantity: 0,
            availableUnits: 0,
            availableQuantity: 0,
            availablePacks: 0,
            weeklyDemand: 15
          });
        }

        if (items.length === 0) {
          setParseError('No valid items found in the CSV. Please ensure each row contains an Item Name.');
          return;
        }

        setParsedItems(items);
      } catch (err) {
        console.error('CSV parse error:', err);
        setParseError('Failed to parse CSV file: ' + err.message);
      }
    };

    reader.onerror = () => {
      setParseError('Error reading file from disk.');
    };

    reader.readAsText(file);
  };

  // ─── Drag and Drop Handlers ────────────────────────────────────────────────
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processCSVFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processCSVFile(e.target.files[0]);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setParsedItems([]);
    setParseError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─── Confirm Import ────────────────────────────────────────────────────────
  const handleConfirmImport = () => {
    if (parsedItems.length === 0) return;
    onImportItems(parsedItems, importMode);
    onClose();
  };

  return (
    <div className="vt-upload-modal-backdrop" onClick={onClose}>
      <div className="vt-upload-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="vt-upload-modal-header">
          <div className="vt-upload-header-left">
            <div className="vt-upload-icon-circle">
              <FaUpload />
            </div>
            <div>
              <h3>Import Purchase Order Items</h3>
              <p>Upload a CSV file containing medicines, batches, quantities and rates</p>
            </div>
          </div>
          <button type="button" className="vt-upload-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <div className="vt-upload-modal-body">
          {/* Reference Sample Download Card */}
          <div className="vt-upload-sample-banner">
            <div className="sample-banner-left">
              <FaFileAlt className="sample-file-icon" />
              <div>
                <strong>Need a reference format?</strong>
                <span>Download our official sample CSV template with pre-filled headers and sample rows.</span>
              </div>
            </div>
            <button
              type="button"
              className="vt-download-sample-btn"
              onClick={handleDownloadSample}
              title="Download Sample CSV Template"
            >
              <FaDownload size={12} /> Download Sample File
            </button>
          </div>

          {/* Upload Dropzone */}
          {!selectedFile ? (
            <div
              className={`vt-upload-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <div className="dropzone-icon-box">
                <FaUpload size={28} />
              </div>
              <h4>Choose a CSV file or drag &amp; drop it here</h4>
              <p>Supports .CSV files with comma-separated values</p>
              <button
                type="button"
                className="dropzone-browse-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  if (fileInputRef.current) fileInputRef.current.click();
                }}
              >
                Browse File
              </button>
            </div>
          ) : (
            <div className="vt-selected-file-card">
              <div className="selected-file-info">
                <FaFileAlt className="file-card-icon" />
                <div className="file-details">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size">
                    {(selectedFile.size / 1024).toFixed(1)} KB • {parsedItems.length} items parsed
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="file-clear-btn"
                onClick={handleClearFile}
                title="Remove file and choose another"
              >
                <FaTrash size={12} /> Remove
              </button>
            </div>
          )}

          {/* Error Message */}
          {parseError && (
            <div className="vt-upload-error-box">
              <FaExclamationTriangle className="error-icon" />
              <span>{parseError}</span>
            </div>
          )}

          {/* Parsed Items Preview Table */}
          {parsedItems.length > 0 && (
            <div className="vt-parsed-preview-section">
              <div className="preview-header-bar">
                <div className="preview-status">
                  <FaCheckCircle className="check-icon" />
                  <strong>Ready to Import {parsedItems.length} Items</strong>
                </div>

                {/* Import Mode: Append or Replace */}
                {existingItemsCount > 0 && (
                  <div className="import-mode-selector">
                    <label className={`mode-pill ${importMode === 'append' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="importMode"
                        value="append"
                        checked={importMode === 'append'}
                        onChange={() => setImportMode('append')}
                      />
                      Append ({existingItemsCount} existing + {parsedItems.length})
                    </label>
                    <label className={`mode-pill ${importMode === 'replace' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="importMode"
                        value="replace"
                        checked={importMode === 'replace'}
                        onChange={() => setImportMode('replace')}
                      />
                      Replace all existing items
                    </label>
                  </div>
                )}
              </div>

              <div className="preview-table-wrapper">
                <table className="preview-items-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th style={{ textAlign: 'left' }}>Item Name</th>
                      <th>Batch</th>
                      <th>Expiry</th>
                      <th>Pack Qty</th>
                      <th>Qty (Packs)</th>
                      <th>Free</th>
                      <th>MRP</th>
                      <th>Rate</th>
                      <th>GST %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedItems.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ textAlign: 'left', fontWeight: '600' }}>{item.itemName}</td>
                        <td style={{ textAlign: 'center' }}>{item.batchNo || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{item.expiryDate || '—'}</td>
                        <td style={{ textAlign: 'center' }}>{item.packQuantity}</td>
                        <td style={{ textAlign: 'center', fontWeight: '700' }}>{item.quantity}</td>
                        <td style={{ textAlign: 'center' }}>{item.freeQuantity}</td>
                        <td style={{ textAlign: 'right' }}>₹{Number(item.mrp || 0).toFixed(2)}</td>
                        <td style={{ textAlign: 'right' }}>₹{Number(item.rate || 0).toFixed(2)}</td>
                        <td style={{ textAlign: 'center' }}>{item.gstPercent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Quick Notice */}
          <div className="vt-upload-quick-notice">
            <FaInfoCircle className="notice-icon" />
            <span>
              Tip: Quantities in the CSV are imported as Pack count. You can switch to Units anytime on the purchase order table.
            </span>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="vt-upload-modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-import-confirm"
            disabled={parsedItems.length === 0}
            onClick={handleConfirmImport}
          >
            <FaUpload size={13} /> Import {parsedItems.length > 0 ? `${parsedItems.length} Items` : 'Items'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadItemsModal;
